import { Quote, ArrowUpRight, Phone, CheckCircle2, Layers, Award, Activity, Search, FileSignature, Cog } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Diff = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  body: string;
  badge?: string;
};
const DIFFERENTIATORS: Diff[] = [{
  icon: Layers,
  title: "Always Replaced as a Matched Pair",
  body: "If one spring failed at 10,000 cycles, its partner is at ~9,500. Replacing one alone means the other snaps within months. We never offer single-spring replacement on a dual-spring door — it's industry best practice, not an upsell.",
  badge: "Best Practice"
}, {
  icon: Cog,
  title: "High-Cycle Steel as Standard",
  body: "We stock 20,000+ cycle commercial-grade springs as our default — not the cheap 10,000-cycle aftermarket versions some installers use. Double the lifespan, same labour cost. You feel the difference in year eight.",
  badge: "20K Cycles"
}, {
  icon: Award,
  title: "AS 4505 Compliant Springs Only",
  body: "Every spring we install meets Australian Standard AS 4505 — galvanised, oil-tempered, force-tested. The certification is documented on your warranty card. Cheap imports don't qualify.",
  badge: "AS 4505"
}, {
  icon: Activity,
  title: "Balance Tested at 4 Positions",
  body: "After install, your door is tested fully closed, fully open, one-third up, and two-thirds up. If it doesn't hold position at all four, we re-tension before we leave. Most installers test once and move on."
}, {
  icon: Search,
  title: "Cable & Drum Inspection Included",
  body: "When a spring snaps, the cables often whip off the drums and the drums can take collateral damage. We inspect both as part of every job, identify wear upfront, and quote any extras before touching them — no surprise charges."
}, {
  icon: FileSignature,
  title: "Workmanship Warranty in Writing",
  body: "12-month written workmanship warranty + manufacturer's warranty on the spring itself. Physical warranty card with our direct number — not just an emailed receipt. If anything fails inside warranty, we come back free.",
  badge: "12 Months"
}];
export default function WhyChooseSpring() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="why-choose-spring-heading">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 15% 30%, ${NAVY_LIGHT}66 0%, transparent 50%), radial-gradient(ellipse at 85% 80%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 md:pt-28 pb-20 md:pb-24">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Why Gold for Spring Work
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            6 Standards · Documented On Every Job
          </div>
        </div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 md:mb-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-stretch gap-4">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD}26 100%)`
            }} />
              <div>
                <Quote className="h-7 w-7 mb-2" strokeWidth={1.5} style={{
                color: GOLD
              } as React.CSSProperties} />
                <h2 id="why-choose-spring-heading" className="font-bold text-white" style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.025em"
              }}>
                  Anyone can swap a spring.{" "}
                  <span style={{
                  color: GOLD
                }}>Few do it right.</span>
                </h2>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <p className="text-[15px] leading-[1.6] mb-5" style={{
            color: "rgba(255,255,255,0.72)"
          }}>
              Cheap installers cut corners that show up in year three.
              We don&rsquo;t. These six standards apply to every spring job we
              do &mdash; documented on every warranty card we hand over.
            </p>

            <a href="tel:0485000933" className="group inline-flex items-center gap-3 text-[14px] font-bold transition-colors" style={{
            color: "white",
            fontFamily: FONT_BODY
          }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "white"}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border" style={{
              borderColor: `${GOLD}66`,
              backgroundColor: `${GOLD}1F`,
              color: GOLD
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
              </span>
              Talk to a spring tech &mdash; 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* Section label */}
        <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-6" style={{
        color: "rgba(255,255,255,0.4)"
      }}>
          &mdash; The six standards
        </div>

        {/* 6-card grid (2 cols) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {DIFFERENTIATORS.map((diff, idx) => <DiffCard key={diff.title} diff={diff} index={idx} />)}
        </div>
      </div>

      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}
function DiffCard({
  diff,
  index
}: {
  diff: Diff;
  index: number;
}) {
  const Icon = diff.icon;
  return <article className="group relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(255,255,255,0.025)",
    borderColor: "rgba(255,255,255,0.08)"
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD}66`;
    e.currentTarget.style.backgroundColor = "rgba(245,166,35,0.04)";
    e.currentTarget.style.transform = "translateY(-2px)";
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.025)";
    e.currentTarget.style.transform = "translateY(0)";
  }}>
      {/* Hover top accent */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`
    }} />

      {/* Watermark number */}
      <div aria-hidden className="absolute -bottom-3 -right-1 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}0F`,
      fontSize: "clamp(4rem, 7vw, 5.5rem)",
      letterSpacing: "-0.04em",
      fontVariantNumeric: "tabular-nums"
    }}>
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="relative p-5 md:p-6">
        {/* Header: icon + badge */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 group-hover:scale-105" style={{
          backgroundColor: `${GOLD}1F`,
          border: `1px solid ${GOLD}40`
        }}>
            <Icon className="h-[19px] w-[19px]" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </div>

          {diff.badge && <span className="inline-flex items-center text-[9px] font-extrabold tracking-[0.18em] uppercase rounded-full px-2.5 py-1 border" style={{
          color: GOLD,
          backgroundColor: `${GOLD}0F`,
          borderColor: `${GOLD}40`,
          fontFamily: FONT_BODY
        }}>
              {diff.badge}
            </span>}
        </div>

        {/* Title */}
        <h3 className="font-extrabold text-white mb-2.5 transition-colors duration-200 group-hover:text-[#F5A623]" style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(1.0625rem, 1.5vw, 1.1875rem)",
        lineHeight: 1.2,
        letterSpacing: "-0.018em"
      }}>
          {diff.title}
        </h3>

        {/* Body */}
        <p className="text-[13.5px] leading-[1.6]" style={{
        color: "rgba(255,255,255,0.68)"
      }}>
          {diff.body}
        </p>
      </div>
    </article>;
}